import { ChevronDown } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CopyIconButton } from '~/components/common/copy-icon-button';
import { Button } from '~/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '~/components/ui/collapsible';
import { cn } from '~/utils/cn';
import { formatDateTime, formatUtcNaiveDateTime } from '~/utils/date';

import { KNOWN_INGEST_REASONS } from '../../constants/enums';
import { type StatusTone, syncStatusTone } from '../../constants/status-tones';
import { destinationOf, useDestinationCopy } from '../../hooks/use-destination-copy';
import type { ProductTimelineEntry, ValidationErrorItem } from '../../types/meta-delivery.types';
import { humanizeEnum } from '../../utils/format-enum';
import { metaFailureLabelForRow } from '../../utils/meta-failure-copy';
import { sentPayload } from '../../utils/payload-diff';
import {
  deriveVerdictKind,
  isEpochSentinel,
  type TimelineVerdictKind,
  verdictTone,
  wasSentToMeta,
} from '../../utils/timeline-verdict';
import { DeliveryStatusChip } from '../chips/delivery-status-chip';
import { DryRunBadge } from '../chips/dry-run-badge';
import { IngestStatusChip } from '../chips/ingest-status-chip';
import { DeliveryLifecycleStrip } from '../product-log/delivery-lifecycle-strip';
import { PayloadComparison } from '../product-log/payload-comparison';

/** Tone → spine dot colour. */
const DOT_COLOR: Record<StatusTone, string> = {
  info: 'bg-info',
  success: 'bg-success',
  warning: 'bg-warning',
  error: 'bg-error',
  muted: 'bg-muted-foreground',
};

/** Tone → the reason band's left rule + wash. One bold element per card. */
const BAND_STYLE: Record<StatusTone, string> = {
  info: 'border-info bg-info-50',
  success: 'border-success bg-success-50',
  warning: 'border-warning bg-warning-50',
  error: 'border-error bg-error-50',
  muted: 'border-muted-foreground/40 bg-muted/40',
};

interface Props {
  entry: ProductTimelineEntry;
  /** Whether the caller asked for payloads — decides the "not captured" vs "not requested" copy. */
  payloadRequested: boolean;
  isLast: boolean;
  /** Open the audit details on mount — used for the newest entry. */
  defaultOpen?: boolean;
  /** The product's previous (older) delivery, for the "unchanged since" / payload diff line. */
  previousEntry?: ProductTimelineEntry | undefined;
}

/**
 * One audit record on the product timeline. Reads top-down as an answer to
 * "why isn't this SKU live?": the verdict headline, then a reason band in plain
 * words (validation errors, Meta's verbatim rejection, the withheld guidance),
 * then the payload's fields with the failing / truncated ones marked in place.
 * Identity, lineage, Meta's verdict and the raw payload sit in the details.
 * A MOCK record is badged as a dry run so it can't read as a real push.
 */
export function ProductTimelineEntryItem({
  entry,
  payloadRequested,
  isLast,
  defaultOpen = false,
  previousEntry,
}: Props) {
  const { t } = useTranslation('deliveries');
  const td = useDestinationCopy(entry.connectorCode);
  const [open, setOpen] = useState(defaultOpen);
  const kind = deriveVerdictKind(entry);
  const tone = verdictTone(kind);
  const isMock = entry.runMode === 'MOCK';

  return (
    <li className="flex gap-3" data-testid={`delivery-timeline-entry-${entry.deliveryJobId}`}>
      <div className="flex flex-col items-center">
        <span className={cn('mt-5 size-2.5 shrink-0 rounded-full', DOT_COLOR[tone])} />
        {!isLast ? <span className="bg-border w-px flex-1" /> : null}
      </div>

      <article
        className={cn('bg-background min-w-0 flex-1 rounded-xl border p-4', !isLast && 'mb-4')}
      >
        {/* Headline: what happened · when · which job */}
        <header className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <DeliveryStatusChip
            tone={tone}
            label={td(`verdict.${kind}`)}
            testId="delivery-timeline-entry-verdict-badge"
          />
          {/* GMC: ingestion (above) and approval are separate facts — a product
              live in Merchant Center can still be disapproved — so approval
              gets its own badge (BE PR #703). */}
          {destinationOf(entry.connectorCode) === 'gmc' && entry.syncStatus ? (
            <DeliveryStatusChip
              tone={syncStatusTone(entry.syncStatus)}
              label={t('timelineEntry.gmc.approval', { status: humanizeEnum(entry.syncStatus) })}
              testId="delivery-timeline-entry-approval-badge"
            />
          ) : null}
          {isMock ? <DryRunBadge /> : null}
          <span className="text-muted-foreground text-xs">
            {t(`timelineEntry.method.${entry.deliveryMethod}`, {
              defaultValue: humanizeEnum(entry.deliveryMethod || 'UNKNOWN'),
            })}
          </span>
          <time
            className="text-muted-foreground text-xs tabular-nums"
            dateTime={entry.deliveryTimestamp}
            title={t('timelineEntry.utcNote')}
          >
            {formatUtcNaiveDateTime(entry.deliveryTimestamp)}
          </time>
          <span className="text-muted-foreground ml-auto inline-flex items-center gap-1 text-xs">
            {t('timelineEntry.job')}
            <span className="font-mono">{shortId(entry.deliveryJobId)}</span>
            <CopyIconButton
              text={entry.deliveryJobId}
              label={t('timelineEntry.copyJob')}
              testId="delivery-timeline-entry-copy-job-button"
            />
          </span>
        </header>

        <DeliveryLifecycleStrip entry={entry} />

        <ReasonBand entry={entry} kind={kind} tone={tone} />

        <FieldGrid entry={entry} />

        {previousEntry ? <PayloadComparison entry={entry} previous={previousEntry} /> : null}

        <Collapsible open={open} onOpenChange={setOpen} className="mt-3">
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="-ml-2 h-7 cursor-pointer gap-1 px-2 text-xs"
              data-testid="delivery-timeline-entry-expand-button"
            >
              <ChevronDown
                aria-hidden="true"
                className={cn('size-3.5 transition-transform', open && 'rotate-180')}
              />
              {open ? t('timelineEntry.hideDetails') : t('timelineEntry.showDetails')}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <EntryDetails entry={entry} payloadRequested={payloadRequested} />
          </CollapsibleContent>
        </Collapsible>
      </article>
    </li>
  );
}

/** First 8 characters of a UUID-ish id — the copy button carries the full value. */
function shortId(id: string): string {
  return id.length > 12 ? `${id.slice(0, 8)}…` : id;
}

/** One validation error as "field — rule: message", field in mono since it is a literal key. */
function ValidationErrorLine({ error }: { error: ValidationErrorItem }) {
  return (
    <li className="text-sm leading-snug">
      {error.field ? <span className="font-mono text-[13px]">{error.field}</span> : null}
      {error.rule ? <span className="text-foreground/70"> — {error.rule}</span> : null}
      {error.message ? (
        <span>
          {error.field || error.rule ? ': ' : ''}
          {error.message}
        </span>
      ) : null}
    </li>
  );
}

/**
 * The cause, in plain words, tinted by the verdict. Validation failures list
 * each error; Meta rejections quote Meta verbatim with its code; withheld rows
 * carry the contract's guidance for the reason; everything else is one sentence.
 * `errorCode` / `errorMessage` follow as a quiet second line when they add anything.
 */
function ReasonBand({
  entry,
  kind,
  tone,
}: {
  entry: ProductTimelineEntry;
  kind: TimelineVerdictKind;
  tone: StatusTone;
}) {
  const { t } = useTranslation('deliveries');
  const td = useDestinationCopy(entry.connectorCode);
  const validationErrors = entry.validationErrors ?? [];
  const knownReason = (KNOWN_INGEST_REASONS as readonly string[]).includes(entry.ingestReason);
  // Our own Meta failure codes (BE PR #814) read as a short headline over the detail.
  const metaLabel = metaFailureLabelForRow(entry, t);

  let primary: ReactNode;
  if (kind === 'validation_failed' && validationErrors.length > 0) {
    primary = (
      <ul className="space-y-1">
        {validationErrors.map((error, i) => (
          <ValidationErrorLine key={`${error.field ?? ''}-${String(i)}`} error={error} />
        ))}
      </ul>
    );
  } else if (kind === 'rejected' || kind === 'transport_failed') {
    primary = (
      <p className="text-sm leading-snug">
        {entry.ingestMessage ? <q className="italic">{entry.ingestMessage}</q> : td(`band.${kind}`)}
        {entry.ingestReason ? (
          <span className="text-foreground/70">
            {' '}
            {td('metaCode', { code: entry.ingestReason })}
          </span>
        ) : null}
      </p>
    );
  } else if ((kind === 'withheld' || kind === 'excluded') && entry.ingestReason) {
    primary = (
      <p className="text-sm leading-snug">
        {knownReason ? t(`ingest.reason.${entry.ingestReason}`) : entry.ingestReason}
        {entry.ingestMessage ? (
          <span className="text-foreground/70"> {entry.ingestMessage}</span>
        ) : null}
      </p>
    );
  } else {
    primary = <p className="text-sm leading-snug">{td(`band.${kind}`)}</p>;
  }

  // Second line: the coarse error fields, unless the band already quoted them.
  const alreadyShown = entry.errorMessage && entry.errorMessage === entry.ingestMessage;
  const secondary =
    !alreadyShown && (entry.errorCode || entry.errorMessage) ? (
      <p className="text-foreground/70 mt-1.5 text-xs">
        {entry.errorMessage}
        {entry.errorCode ? <span className="font-mono"> ({entry.errorCode})</span> : null}
      </p>
    ) : null;

  return (
    <div
      className={cn('mt-3 rounded-md border-l-2 py-2 pr-3 pl-3', BAND_STYLE[tone])}
      data-testid="delivery-timeline-entry-reason"
    >
      {metaLabel ? <p className="mb-1 text-xs font-semibold">{metaLabel}</p> : null}
      {primary}
      {secondary}
    </div>
  );
}

/**
 * The payload's fields as chips, with the ones that failed validation or
 * assembly marked in error and the truncated ones in warning — so the problem
 * shows where the data is, and the empty "fields failed / truncated" blocks
 * are unnecessary. The heading says "sent" only when the item reached Meta.
 */
function FieldGrid({ entry }: { entry: ProductTimelineEntry }) {
  const { t } = useTranslation('deliveries');
  const td = useDestinationCopy(entry.connectorCode);
  const sent = wasSentToMeta(entry);
  const failed = new Map<string, string>();
  for (const error of entry.validationErrors ?? []) {
    if (error.field)
      failed.set(error.field, [error.rule, error.message].filter(Boolean).join(': '));
  }
  for (const field of entry.fieldsFailed ?? []) {
    if (!failed.has(field))
      failed.set(field, entry.fieldErrors?.[field] ?? t('timelineEntry.assemblyFailed'));
  }
  const truncated = new Set(entry.truncatedFields ?? []);
  const fields = [...(entry.deliveredKeys ?? [])];
  for (const field of failed.keys()) if (!fields.includes(field)) fields.push(field);

  if (fields.length === 0) return null;

  return (
    <section className="mt-3">
      <h4 className="text-muted-foreground mb-1.5 text-xs">
        {sent
          ? t('timelineEntry.fieldsSent', { count: fields.length })
          : t('timelineEntry.fieldsInPayload', { count: fields.length })}
        {failed.size > 0
          ? ` · ${t('timelineEntry.fieldsFailedCount', { count: failed.size })}`
          : ''}
        {truncated.size > 0
          ? ` · ${t('timelineEntry.fieldsTruncatedCount', { count: truncated.size })}`
          : ''}
      </h4>
      <ul className="flex flex-wrap gap-1.5">
        {fields.map((field) => {
          const failure = failed.get(field);
          const isTruncated = truncated.has(field);
          return (
            <li
              key={field}
              className={cn(
                'rounded-md border px-2 py-0.5 font-mono text-[11px] leading-5',
                failure
                  ? 'border-error/40 bg-error-50 text-error'
                  : isTruncated
                    ? 'border-warning/40 bg-warning-50 text-warning'
                    : 'border-border/60 bg-muted/50 text-foreground',
              )}
              title={failure ?? (isTruncated ? td('truncatedHint') : undefined)}
            >
              {field}
            </li>
          );
        })}
      </ul>
      {truncated.size > 0 ? (
        <p className="text-muted-foreground mt-1.5 text-xs">{td('truncatedHint')}</p>
      ) : null}
    </section>
  );
}

/** Sentence-case block heading inside the details grid. */
function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <h4 className="text-muted-foreground mb-1.5 text-xs">{title}</h4>
      {children}
    </div>
  );
}

/**
 * Label / value row. Values sit left, right after the label column; ids wrap
 * rather than truncate (only mono ids break mid-word, so chips and prose don't).
 * `copy` adds a copy button for identifiers.
 */
function Fact({
  label,
  value,
  mono = false,
  copy,
}: {
  label: string;
  value: ReactNode;
  mono?: boolean;
  copy?: string | null | undefined;
}) {
  const { t } = useTranslation('deliveries');
  return (
    <div className="grid grid-cols-[7.5rem_1fr] gap-x-3 text-xs">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={cn('text-foreground min-w-0', mono && 'font-mono break-all')}>
        {copy ? (
          <span className="flex items-start gap-1">
            <span className="min-w-0">{value}</span>
            <CopyIconButton
              text={copy}
              label={t('timelineEntry.copyValue', { field: label })}
              className="-my-0.5 size-5 shrink-0"
            />
          </span>
        ) : (
          value
        )}
      </dd>
    </div>
  );
}

/** Identity + lineage, Meta's verdict, the raw statuses and the payload — the full audit. */
function EntryDetails({
  entry,
  payloadRequested,
}: {
  entry: ProductTimelineEntry;
  payloadRequested: boolean;
}) {
  const { t } = useTranslation('deliveries');
  const td = useDestinationCopy(entry.connectorCode);
  const dash = <span className="text-muted-foreground">—</span>;
  const sent = wasSentToMeta(entry);
  const verdictAt = isEpochSentinel(entry.syncStatusAt) ? null : entry.syncStatusAt;

  return (
    <div
      className="mt-2 grid gap-5 border-t pt-4 sm:grid-cols-2"
      data-testid="delivery-timeline-entry-details"
    >
      <Block title={t('timelineEntry.blocks.identity')}>
        <dl className="space-y-1">
          <Fact
            label={t('timelineEntry.fields.externalId')}
            value={entry.externalId || dash}
            mono
            copy={entry.externalId}
          />
          <Fact
            label={t('timelineEntry.fields.sourceProductId')}
            value={entry.sourceProductId || dash}
            mono
            copy={entry.sourceProductId}
          />
          <Fact
            label={t('timelineEntry.fields.connectorCode')}
            value={entry.connectorCode ? humanizeEnum(entry.connectorCode) : dash}
          />
          <Fact
            label={t('timelineEntry.fields.runMode')}
            value={entry.runMode ? humanizeEnum(entry.runMode) : dash}
          />
          <Fact label={t('timelineEntry.fields.attemptCount')} value={String(entry.attemptCount)} />
        </dl>
      </Block>

      <Block title={t('timelineEntry.blocks.lineage')}>
        <dl className="space-y-1">
          <Fact
            label={t('timelineEntry.fields.enrichmentWorkflowId')}
            value={entry.enrichmentWorkflowId || dash}
            mono
            copy={entry.enrichmentWorkflowId}
          />
          <Fact
            label={t('timelineEntry.fields.unitIndex')}
            value={entry.unitIndex != null ? String(entry.unitIndex) : dash}
          />
          <Fact
            label={t('timelineEntry.fields.repushGeneration')}
            value={
              entry.previousDeliveryJobId
                ? t('timelineEntry.supersedes', {
                    job: shortId(entry.previousDeliveryJobId),
                    generation: entry.repushGeneration,
                  })
                : t('timelineEntry.firstDelivery')
            }
            copy={entry.previousDeliveryJobId}
          />
          <Fact
            label={t('timelineEntry.fields.payloadHash')}
            value={entry.payloadHash || dash}
            mono
            copy={entry.payloadHash}
          />
        </dl>
      </Block>

      <Block title={td('blocks.destination')}>
        {!sent ? (
          <p className="text-foreground/80 text-xs">{td('notSentNoVerdict')}</p>
        ) : entry.syncStatus ? (
          <div className="space-y-1.5">
            <DeliveryStatusChip
              tone={syncStatusTone(entry.syncStatus)}
              label={humanizeEnum(entry.syncStatus)}
            />
            {entry.syncStatusMessage ? (
              <p className="text-foreground/80 text-xs">
                <q className="italic">{entry.syncStatusMessage}</q>
                {entry.syncStatusReason ? (
                  <span className="font-mono"> ({entry.syncStatusReason})</span>
                ) : null}
              </p>
            ) : entry.syncStatusReason ? (
              <p className="font-mono text-xs">{entry.syncStatusReason}</p>
            ) : null}
            <dl className="space-y-1">
              <Fact
                label={t('timelineEntry.fields.syncStatusAt')}
                value={verdictAt ? formatDateTime(verdictAt) : td('noVerdictYet')}
              />
              <Fact
                label={td('fields.syncExternalId')}
                value={entry.syncExternalId || dash}
                mono
                copy={entry.syncExternalId}
              />
            </dl>
          </div>
        ) : (
          <p className="text-foreground/80 text-xs">{td('noVerdictYet')}</p>
        )}
      </Block>

      <Block title={t('timelineEntry.blocks.rawStatuses')}>
        <dl className="space-y-1">
          <Fact
            label={t('timelineEntry.fields.ingestStatus')}
            value={
              <IngestStatusChip
                status={entry.ingestStatus}
                destination={destinationOf(entry.connectorCode)}
              />
            }
          />
          <Fact
            label={t('timelineEntry.fields.ingestReason')}
            value={entry.ingestReason || dash}
            mono
            copy={entry.ingestReason}
          />
          <Fact
            label={t('timelineEntry.fields.outcome')}
            value={entry.outcome ? humanizeEnum(entry.outcome) : dash}
          />
          <Fact
            label={t('timelineEntry.fields.validationStatus')}
            value={entry.validationStatus ? humanizeEnum(entry.validationStatus) : dash}
          />
        </dl>
      </Block>

      <Block title={t('timelineEntry.blocks.payload')}>
        {/* Reserved `_` keys (display-only identity) were stored, not sent — hide them. */}
        {entry.transformedData ? (
          <div className="relative">
            <CopyIconButton
              text={JSON.stringify(sentPayload(entry.transformedData), null, 2)}
              label={t('timelineEntry.copyPayload')}
              className="bg-background/80 absolute top-1 right-1 size-6"
            />
            <pre
              data-testid="delivery-timeline-entry-payload"
              className="bg-muted/40 max-h-64 overflow-auto rounded-md p-2 pr-8 text-[11px] leading-snug"
            >
              {JSON.stringify(sentPayload(entry.transformedData), null, 2)}
            </pre>
          </div>
        ) : (
          <p className="text-muted-foreground text-xs">
            {payloadRequested
              ? t('timelineEntry.payloadNotCaptured')
              : t('timelineEntry.payloadNotRequested')}
          </p>
        )}
      </Block>
    </div>
  );
}
